/* @flow */
import * as React from 'react';
import { observer, inject } from 'mobx-react';
import { reaction, computed } from 'mobx';
import { Link } from 'react-router-dom';
import { translate } from 'react-i18next';
import type { TranslatorProps } from 'react-i18next';
import RequireForAccess from 'react-access';

import Button from '@material-ui/core/Button';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import CardHeader from '@material-ui/core/CardHeader';
import Divider from '@material-ui/core/Divider';

import Loading from 'components/Loading/Loading';
import DashboardStore from 'stores/DashboardStore';
import { createNewAccount } from 'permissions/account.js';
import { ComponentWithInjectedProps } from 'utils/injectedProps';
import * as statuses from 'utils/statuses';

import { isIE } from '../../../../utils/browserDetection';
import ChartLegend from '../../components/ChartLegend/ChartLegend';
import ChartKeys from '../../components/ChartKeys';
import WidgetActions from '../../components/WidgetActions';
import NivoLineChart from '../../components/NivoLineChart';
import type { CommonWidgetProps } from '../../commonTypes';

import styles from './TradingAccounts.module.scss';

const STATIC_DATA = {
  accountsByTime: {
    '2020-12-03': {
      users: 4,
      logins: 191,
      assets: 28,
    },
    '2019-03-04': {
      users: 1,
      logins: 50,
      assets: 35,
    },
    '2019-03-16': {
      users: 2,
      logins: 93,
      assets: 13,
    },
    '2018-11-12': {
      users: 2,
      logins: 100,
      assets: 69,
    },
    '2019-11-05': {
      users: 1,
      logins: 53,
      assets: 0,
    },
    '2018-09-21': {
      users: 1,
      logins: 38,
      assets: 60,
    },
    '2021-02-03': {
      users: 1,
      logins: 57,
      assets: 0,
    },
    '2020-11-10': {
      users: 2,
      logins: 112,
      assets: 43,
    },
    '2021-03-02': {
      users: 4,
      logins: 175,
      assets: 93,
    },
  },
  accounts: [],
};

export type TradingAccountsProps = CommonWidgetProps;

type InjectedProps = {
  dashboardStore: DashboardStore,
} & TranslatorProps;

type State = {
  status: string,
  data: any[],
};

@translate()
@inject('dashboardStore')
@observer
export default class TradingAccounts extends ComponentWithInjectedProps<
  TradingAccountsProps,
  State,
  InjectedProps
> {
  static LOAD_THUMBNAIL = false;

  @computed get shouldLoadData(): boolean {
    const { size } = this.props;

    return size !== 'thumbnail' || TradingAccounts.LOAD_THUMBNAIL;
  }

  @computed get widgetData(): { accountsByTime: any | null, accounts: any[] | null } {
    const { dashboardStore } = this.props;

    if (!this.shouldLoadData) {
      return STATIC_DATA;
    }

    return {
      accountsByTime:
        dashboardStore && dashboardStore.accountsbytime
          ? dashboardStore.accountsbytime['trading-accounts']
          : null,
      accounts:
        dashboardStore.accounts && dashboardStore.accounts
          ? dashboardStore.accounts['trading-accounts']
          : null,
    };
  }

  state = {
    status: 'Week',
    data: [],
  };

  autoUpdateDisposer: Function;

  componentDidMount() {
    const { dashboardStore } = this.props;

    this.loadWidgetData(dashboardStore.loadedTradingAccount, this.state.status);

    this.autoUpdateDisposer = reaction(
      () => dashboardStore.loadedTradingAccount,
      ta => {
        this.loadWidgetData(ta, this.state.status);
      }
    );
  }

  loadWidgetData = (tradingAccount: string, status: string) => {
    const { dashboardStore } = this.props;

    if (this.shouldLoadData) {
      dashboardStore.loadTradingAccounts(tradingAccount);
      this.changeData(status);
    }
  };

  componentWillUnmount() {
    this.autoUpdateDisposer();
  }

  getWeek = (time: Date) => {
    const onejan = new Date(time.getFullYear(), 0, 1);
    return Math.ceil(((time - onejan) / 86400000 + onejan.getDay() + 1) / 7);
  };

  async changeData(timePeriod: string) {
    const { dashboardStore } = this.props;

    this.setState({ status: timePeriod });

    await dashboardStore.loadTradingAccounts(dashboardStore.loadedTradingAccount, timePeriod);

    if (timePeriod === 'Week') {
      this.displayWeekView();
    }

    if (timePeriod === 'Month') {
      this.displayMonthView();
    }

    if (timePeriod === 'Year') {
      this.displayYearView();
    }
  }

  displayWeekView() {
    const { dashboardStore, t } = this.props;
    const assets = [];
    const users = [];
    const logins = [];
    const { accountsByTime } = this.widgetData;

    const dates = [];

    for (let i = 1; i <= 7; i++) {
      const day = new Date();
      day.setDate(day.getDate() - day.getDay() + i);
      dates.push(
        day.getFullYear() +
          '-' +
          ('0' + (day.getMonth() + 1)).slice(-2) +
          '-' +
          ('0' + day.getDate()).slice(-2)
      );
    }

    dates.map(date => {
      const dateAssets = {
        x: date,
        y: accountsByTime && accountsByTime[date] ? accountsByTime[date].assets : 0,
      };

      const dateUsers = {
        x: date,
        y: accountsByTime && accountsByTime[date] ? accountsByTime[date].users : 0,
      };

      const dateLogins = {
        x: date,
        y: accountsByTime && accountsByTime[date] ? accountsByTime[date].logins : 0,
      };

      assets.push(dateAssets);
      users.push(dateUsers);
      logins.push(dateLogins);

      return assets;
    });

    const weekTrading = [
      {
        id: t('general:assets.total_assets'),
        color: 'hsl(12, 74%, 59%)',
        data: assets,
      },
      {
        id: t('general:login_plural'),
        color: 'hsl(198, 93%, 38%)',
        data: logins,
      },
      {
        id: t('general:user_plural'),
        color: 'hsl(200, 7%, 67%)',
        data: users,
      },
    ];

    this.setState({
      data: weekTrading,
    });
  }

  displayMonthView() {
    const { dashboardStore, t } = this.props;
    const assets = [];
    const users = [];
    const logins = [];
    const { accountsByTime } = this.widgetData;

    const time = new Date();
    const y = time.getFullYear();
    const m = time.getMonth();
    const dates = [];

    const firstWeekOfMonth = this.getWeek(new Date(y, m, 1));
    const lastWeekOfMonth = this.getWeek(new Date(y, m + 1, 0));

    for (let i = firstWeekOfMonth; i < lastWeekOfMonth; i++) {
      dates.push(i);
    }

    dates.map(date => {
      const dateAssets = {
        x: 'Week ' + date,
        y: accountsByTime && accountsByTime[date] ? accountsByTime[date].assets : 0,
      };

      const dateUsers = {
        x: 'Week ' + date,
        y: accountsByTime && accountsByTime[date] ? accountsByTime[date].users : 0,
      };

      const dateLogins = {
        x: 'Week ' + date,
        y: accountsByTime && accountsByTime[date] ? accountsByTime[date].logins : 0,
      };

      assets.push(dateAssets);
      users.push(dateUsers);
      logins.push(dateLogins);

      return assets;
    });

    const monthTrading = [
      {
        id: t('general:assets.total_assets'),
        color: 'hsl(12, 74%, 59%)',
        data: assets,
      },
      {
        id: t('general:login_plural'),
        color: 'hsl(198, 93%, 38%)',
        data: logins,
      },
      {
        id: t('general:user_plural'),
        color: 'hsl(200, 7%, 67%)',
        data: users,
      },
    ];

    this.setState({
      data: monthTrading,
    });
  }

  displayYearView() {
    const { dashboardStore, t } = this.props;
    const assets = [];
    const users = [];
    const logins = [];
    const { accountsByTime } = this.widgetData;

    const time = new Date();
    const dates = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
    const year = time.getFullYear();

    dates.map(date => {
      const dateAssets = {
        x: date + '/' + year,
        y: accountsByTime && accountsByTime[date] ? accountsByTime[date].assets : 0,
      };

      const dateUsers = {
        x: date + '/' + year,
        y: accountsByTime && accountsByTime[date] ? accountsByTime[date].users : 0,
      };

      const dateLogins = {
        x: date + '/' + year,
        y: accountsByTime && accountsByTime[date] ? accountsByTime[date].logins : 0,
      };

      assets.push(dateAssets);
      users.push(dateUsers);
      logins.push(dateLogins);

      return assets;
    });

    const yearTrading = [
      {
        id: t('general:assets.total_assets'),
        color: 'hsl(12, 74%, 59%)',
        data: assets,
      },
      {
        id: t('general:login_plural'),
        color: 'hsl(198, 93%, 38%)',
        data: logins,
      },
      {
        id: t('general:user_plural'),
        color: 'hsl(200, 7%, 67%)',
        data: users,
      },
    ];

    this.setState({
      data: yearTrading,
    });
  }

  render() {
    const { dashboardStore, t, i18n, widgetItem, size, isFullScreen } = this.props;
    const { accounts } = this.widgetData;

    let active = 0;
    let disabled = 0;
    let archived = 0;

    if (accounts) {
      for (let i = accounts.length - 1; i >= 0; i--) {
        if (accounts[i].is_enabled) {
          active += 1;
        } else {
          disabled += 1;
        }

        if (accounts[i].archived_at) {
          archived += 1;
        }
      }
    }

    return (
      <Card className={styles[this.props.size]}>
        <CardHeader
          title={
            dashboardStore.loadedTradingAccount
              ? t('general:trading_account')
              : t('general:trading_account_plural')
          }
          action={<WidgetActions widgetItem={widgetItem} size={size} isFullScreen={isFullScreen} />}
          className={styles['card-header']}
          classes={{ action: styles.cardHeaderAction }}
        />
        {(!isIE || this.props.size !== 'thumbnail') && (
          <React.Fragment>
            <Divider />
            <CardContent className={styles['card-content']}>
              {this.props.size === 'thumbnail' ? null : (
                <ChartLegend
                  position="center"
                  keys={[
                    {
                      label: t('general:assets.total_assets'),
                      color: 'hsl(12, 74%, 59%)',
                    },
                    {
                      label: t('general:login_plural'),
                      color: 'hsl(198, 93%, 38%)',
                    },
                    {
                      label: t('general:user_plural'),
                      color: 'hsl(200, 7%, 67%)',
                    },
                  ]}
                />
              )}
              <div className={styles['line-chart-container']}>
                {this.shouldLoadData && dashboardStore.loadingTradingAccounts ? (
                  <Loading />
                ) : (
                  <React.Fragment>
                    {this.props.size !== 'canvas-50' && (
                      <ChartKeys
                        widgetSize={this.props.size}
                        showPlaceholders={!this.shouldLoadData}
                        keys={[
                          {
                            to: { pathname: '/accounts', state: { tab: 0 } },
                            label: statuses.translate('active', i18n),
                            total: active,
                          },
                          {
                            to: { pathname: '/accounts', state: { tab: 1 } },
                            label: statuses.translate('disabled', i18n),
                            total: disabled,
                          },
                          {
                            to: { pathname: '/accounts', state: { tab: 2 } },
                            label: statuses.translate('archived', i18n),
                            total: archived,
                          },
                        ]}
                      />
                    )}
                    <div className={styles['line-chart']}>
                      <NivoLineChart data={this.state.data} widgetSize={this.props.size} />
                    </div>
                  </React.Fragment>
                )}
              </div>
              {this.props.size === 'thumbnail' ? null : (
                <div>
                  <ul className={styles['line-chart-filters']}>
                    <li
                      onClick={(e: any) => this.changeData('Week')}
                      type="week"
                      className={this.state.status === 'Week' ? styles['filter-active'] : null}
                    >
                      {t('general:labels.week')}
                    </li>
                    <li
                      onClick={(e: any) => this.changeData('Month')}
                      type="month"
                      className={this.state.status === 'Month' ? styles['filter-active'] : null}
                    >
                      {t('general:labels.month')}
                    </li>
                    <li
                      onClick={(e: any) => this.changeData('Year')}
                      type="year"
                      className={this.state.status === 'Year' ? styles['filter-active'] : null}
                    >
                      {t('general:labels.year')}
                    </li>
                  </ul>
                </div>
              )}
            </CardContent>
          </React.Fragment>
        )}
        {this.props.size !== 'thumbnail' && (
          <div className={styles['button-container']}>
            <RequireForAccess permissions={createNewAccount}>
              <Link to="accounts/create">
                <Button color="primary" variant="contained">
                  {t('general:actions.add_trading_account_plural')}
                </Button>
              </Link>
            </RequireForAccess>
          </div>
        )}
      </Card>
    );
  }
}
